import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Spinner } from '@finn-auto/design-system/atoms/spinner';
import {
  fetchCandidateEmails,
  HistoryRefusedError,
  type CandidateEmailHistory,
  type CandidateEmailMessage,
  type CandidateEmailThread,
} from '../../services/taCandidateEmailsApi';
import { COLORS, fmtDate } from './format';
import { useOptionalGmailConnection } from './GmailConnectionContext';

/**
 * The candidate's email history, fetched with react-query and ONLY while the modal is open.
 *
 * The Gmail token is read through a ref so a reconnect can refetch with the new one, and the key
 * carries only whether a token exists — never the token itself.
 */
export function useCandidateEmails({
  boardEmail,
  opportunityId,
  open,
}: {
  boardEmail: string;
  opportunityId: string;
  open: boolean;
}) {
  const gmail = useOptionalGmailConnection();
  const tokenRef = useRef<string | null>(gmail?.token ?? null);
  useEffect(() => {
    tokenRef.current = gmail?.token ?? null;
  }, [gmail?.token]);
  const query = useQuery<CandidateEmailHistory, Error>({
    queryKey: ['candidate-emails', boardEmail, opportunityId, Boolean(gmail?.token)],
    queryFn: () => fetchCandidateEmails({ opportunityId, boardEmail, gmailToken: tokenRef.current }),
    enabled: open,
    staleTime: 60_000,
    retry: (count, error) => !(error instanceof HistoryRefusedError) && count < 1,
  });
  // Named fields only, so react-query tracks (and re-renders on) just what the modal reads.
  const { data, error, isPending, refetch } = query;
  return { data, error, isPending, refetch, gmail };
}

const personLabel = (m: CandidateEmailMessage) => m.from.name || m.from.email || 'Unknown sender';

/**
 * One message, collapsed to a preview. The header is a plain <button> on purpose: it is a
 * full-width disclosure ROW (sender, date, badge, preview), not a labelled action, the same shape
 * as the cockpit's other disclosure rows — the DS `Button`'s padding and centred label cannot
 * carry it.
 */
function MessageRow({ message }: { message: CandidateEmailMessage }) {
  const [open, setOpen] = useState(false);
  const preview = (message.body || '').replace(/\s+/g, ' ').trim();
  return (
    <div className="rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full flex-col items-start gap-1 text-left"
      >
        <span className="flex w-full flex-wrap items-center gap-2 text-xs text-gray-500">
          <span className="font-semibold text-gray-800">{personLabel(message)}</span>
          {message.date && <span>{fmtDate(message.date)}</span>}
          {message.automatic && (
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ backgroundColor: COLORS.blueBg, color: COLORS.blueText }}
              title="Sent automatically from interview.scheduling@finn.com"
            >
              automatic
            </span>
          )}
          <span className="ml-auto text-gray-400">{open ? '▾' : '▸'}</span>
        </span>
        {!open && (
          <span className="line-clamp-1 text-sm text-gray-700">{preview || message.subject || '(no text)'}</span>
        )}
      </button>
      {open && (
        <div className="mt-2 space-y-1">
          <div className="text-xs text-gray-500">
            To {message.to.join(', ') || '—'}
            {message.cc.length > 0 && ` · Cc ${message.cc.join(', ')}`}
          </div>
          {/* Candidate email is untrusted text: rendered as text, never as markup. */}
          <div className="whitespace-pre-wrap text-sm text-gray-900">
            {message.body || 'No text was logged for this email — only its subject.'}
          </div>
        </div>
      )}
    </div>
  );
}

function ThreadGroup({ thread }: { thread: CandidateEmailThread }) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
          {thread.subject || '(no subject)'}
        </h3>
        {thread.gmailUrl && (
          <a href={thread.gmailUrl} target="_blank" rel="noreferrer" className="shrink-0 text-xs text-blue-600 hover:underline">
            Open in Gmail ↗
          </a>
        )}
      </div>
      {thread.messages.map((m) => (
        <MessageRow key={m.id} message={m} />
      ))}
    </section>
  );
}

export function EmailHistoryModal({
  boardEmail,
  opportunityId,
  candidateName,
  onClose,
}: {
  boardEmail: string;
  opportunityId: string;
  candidateName: string;
  onClose: () => void;
}) {
  const { data, error, isPending, refetch, gmail } = useCandidateEmails({ boardEmail, opportunityId, open: true });
  const [reconnectNonce, setReconnectNonce] = useState(0);
  // Refetch once the reconnected token has landed in the ref (after the re-render).
  useEffect(() => {
    if (reconnectNonce > 0) void refetch();
  }, [reconnectNonce, refetch]);

  const connect = async () => {
    if (!gmail) return;
    if (await gmail.connect()) setReconnectNonce((n) => n + 1);
  };
  const gmailHalf = data?.gmail;
  const needsConnect = gmailHalf && !gmailHalf.ok && (gmailHalf.code === 'needs-connect' || gmailHalf.code === 'reconnect');

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,44rem)] gap-5 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle>✉️ Emails — {candidateName}</ModalTitle>
        </ModalHeader>

        {isPending && (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner className="h-3 w-3" /> Loading the email history…
          </p>
        )}

        {error instanceof HistoryRefusedError && (
          <p className="text-sm text-gray-600">{error.message}.</p>
        )}
        {error && !(error instanceof HistoryRefusedError) && (
          <div className="space-y-2">
            <p className="text-sm text-red-600">Could not load the email history just now.</p>
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        )}

        {data && gmailHalf && !gmailHalf.ok && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg p-3 text-sm" style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}>
            <span className="flex-1">
              {needsConnect
                ? 'Connect your Gmail to see your email history with this candidate.'
                : gmailHalf.error}{' '}
              {data.threads.length > 0 && 'Showing the automatic emails only.'}
            </span>
            {needsConnect && gmail && (
              <Button size="sm" variant="outline" onClick={() => void connect()} loading={gmail.connecting}>
                Connect Gmail
              </Button>
            )}
          </div>
        )}

        {data && data.threads.length === 0 && (gmailHalf?.ok || !needsConnect) && (
          <p className="text-sm text-gray-500">No emails with this candidate in your mailbox.</p>
        )}

        {data && data.threads.length > 0 && (
          <div className="space-y-5">
            {data.threads.map((t) => (
              <ThreadGroup key={t.id} thread={t} />
            ))}
            {gmailHalf?.ok && gmailHalf.truncated && (
              <p className="text-xs text-gray-400">Showing the 25 most recent threads — older ones are in Gmail.</p>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
